import { SpineGameObject } from "@esotericsoftware/spine-phaser";
import ResourcesDispatcher from "../../config/ResourcesDispatcher";

export default class LinkCell {
    public scene: any;

    private container: Phaser.GameObjects.Container;

    private memo: number = 0;
    private index: number;

    public x: number;
    public y: number;

    private linkSpineWithText: SpineGameObject;
    private linkSpineWithValue: SpineGameObject;
    private linkSpineValueText: Phaser.GameObjects.Text;
    public linkSpinTextValueTween: Phaser.Tweens.Tween | undefined;

    private isStopped: boolean = false;

    private linkSpinSymbolSpineRaw: any;

    private linkSpinParticleAnimation: SpineGameObject;
    private linkSpinSymbolSpines: SpineGameObject[];
    private background: Phaser.GameObjects.Image;

    public multValue!: number;
    public spinTween?: Phaser.Tweens.Tween;
    public celebrateLinkResolve: ((value: boolean | PromiseLike<boolean>) => void) | undefined;
    public timerCelebrateLinkWin: Phaser.Time.TimerEvent | undefined;

    constructor(scene: any, x: number, y: number, index: number) {
        this.scene = scene;
        
        this.x = x;
        this.y = y;
        this.index = index;

        this.linkSpinSymbolSpineRaw = ResourcesDispatcher.GAME_CONSTANTS.linkSymbolSpines;

        this.linkSpinSymbolSpines = [];
        for (let i = 0; i < this.linkSpinSymbolSpineRaw.length; i++) {
            let spine = this.scene.add.spine(0, 0, this.getLinkSpinSymbolSpineRaw(i).jsonKey, this.getLinkSpinSymbolSpineRaw(i).atlasKey).setOrigin(0.5).setVisible(false);
            this.linkSpinSymbolSpines.push(spine);
        }

        this.container = this.scene.add.container(this.x, this.y, [...this.linkSpinSymbolSpines]);

        this.background = this.scene.add.image(0, 0, ResourcesDispatcher.ATLAS.LINKCELL_BACKGROUND.key, ResourcesDispatcher.ATLAS.LINKCELL_BACKGROUND.frameKey).setOrigin(0.5).setVisible(false);
        this.linkSpinParticleAnimation = this.scene.add.spine(0, 0, ResourcesDispatcher.SPINES.LinkSpin_Particle_Animation.jsonKey, ResourcesDispatcher.SPINES.LinkSpin_Particle_Animation.atlasKey).setScale(.2).setOrigin(0.5).setVisible(false);

        this.linkSpineWithText = this.linkSpinSymbolSpines[ResourcesDispatcher.GAME_CONSTANTS.S_LINK];
        this.linkSpineWithValue = this.scene.add.spine(0, 0, ResourcesDispatcher.SPINES.LinkSpin.jsonKey, ResourcesDispatcher.SPINES.LinkSpin.atlasKey).setOrigin(0.5).setVisible(false);

        let linkFont = this.scene.globalVariableManager.getFont().overrideFont({fontSize: 50,color: "#FFEE00"});
        this.linkSpineValueText = this.scene.add.text(0, 0, "", linkFont).setOrigin(0.5).setVisible(false);
        
        this.container.add([this.linkSpineWithValue, this.linkSpineValueText, this.linkSpinParticleAnimation]);
        this.container.addAt(this.background, 0);
    }

    public setIndex(index: number) {
        this.index = index;
    }

    public setMemo(memo: number): void {
        this.memo = memo;
    }

    /** PUBLIC GETTERS **/
    public getIndex(): number {
        return this.index;
    }

    public getMemo(): number {
        return this.memo;
    }

    public getContainer(): Phaser.GameObjects.Container {
        return this.container;
    }   

    public getSpine(memo: number): SpineGameObject {
        return this.linkSpinSymbolSpines[memo];
    }

    public getBackground(): Phaser.GameObjects.Image {
        return this.background;
    }

    public getLinkSpineWithText(): SpineGameObject {
        return this.linkSpineWithText;
    }

    public getLinkSpineWithValue(): SpineGameObject {
        return this.linkSpineWithValue;
    }

    public getLinkSpineValueText(): Phaser.GameObjects.Text {
        return this.linkSpineValueText;
    }
    /*******************/

    /** PRIVATE GETTERS **/
    private getLinkSpinSymbolSpineRaw(memo: number): any {
        return this.linkSpinSymbolSpineRaw[memo];
    }
    /*******************/
    
    public setCellInfo(cellMemo: number): LinkCell {
        this.linkSpinSymbolSpines.forEach((spine) => {
            spine.setVisible(false);
        });

        this.linkSpineValueText.setScale(1);

        this.getSpine(cellMemo).setVisible(true);
        this.getSpine(cellMemo).animationState.clearTrack(0);
        this.getSpine(cellMemo).skeleton.setToSetupPose();

        this.setMemo(cellMemo);

        return this;
    }

    public playAnimation(loop: boolean = false): Promise<boolean> {
        return new Promise((resolve) => {

            const memo = this.getMemo();

            // Spine principale (animazione 0)
            const mainSpine = this.getSpine(memo);
            const mainAnimations = this.getLinkSpinSymbolSpineRaw(memo).animations;

            if (!mainSpine || !mainAnimations || !mainAnimations[0]) {
                return resolve(true);
            }

            mainSpine.setVisible(true);

            //-----------------------------------------
            // 1) RIPRODUCO SEMPRE ANIMAZIONE 0 SUL MAIN SPINE
            //-----------------------------------------
            const baseEntry = mainSpine.animationState.addAnimation(0, mainAnimations[0], false);

            if (baseEntry) baseEntry.alpha = 1;

            //-----------------------------------------
            // 2) SE IL SIMBOLO È 13 / 14 / 15 → AGGIUNGO OVERLAY
            //-----------------------------------------
            if (memo === 13 || memo === 14 || memo === 15) {

                // Prendo lo spine overlay (ultimo della lista)
                const overlayIndex = this.linkSpinSymbolSpineRaw.length - 1;
                const overlaySpine = this.getSpine(overlayIndex);
                const overlayAnimations = this.getLinkSpinSymbolSpineRaw(overlayIndex).animations;

                if (overlaySpine && overlayAnimations) {

                    const OVERLAY_MAP: Record<number, string> = {
                        13: "mini_enter",
                        14: "major_enter",
                        15: "grand_enter"
                    };

                    const overlayName = OVERLAY_MAP[memo];
                    const overlayAnimIndex = overlayAnimations.indexOf(overlayName);

                    if (overlayAnimIndex !== -1) {

                        overlaySpine.setVisible(true);

                        // Overlay gira in loop sul suo track 1 (spine SEPARATO)
                        const overlayEntry = overlaySpine.animationState.addAnimation(1, overlayAnimations[overlayAnimIndex], false);

                        if (overlayEntry) overlayEntry.alpha = 1;
                    }
                }
            }

            //-----------------------------------------
            // 3) Listener per concludere la Promise
            //-----------------------------------------
            if (!loop && baseEntry) {
                baseEntry.listener = {
                    complete: () => resolve(true)
                };
            } else {
                resolve(true);
            }
        });
    }

    public stopAnimation(): void {
        this.getSpine(this.getMemo()).animationState.clearTrack(0);
        this.getSpine(this.getMemo()).skeleton.setToSetupPose();
    }  

    public playLinkSpinParticleAnimation(): Promise<boolean> {
        return new Promise(async (resolve) => {
            this.linkSpinParticleAnimation.setVisible(true);
            const animations = this.getLinkSpinSymbolSpineRaw(18).animations;

            if (animations) {
                this.linkSpinParticleAnimation.animationState.timeScale = 0.8;
                this.linkSpinParticleAnimation.animationState.setAnimation(0, animations[0], false);
            }

            const animationStateListener : any= {
                complete: async () => {
                    // Remove this specific listener after it is called
                    this.linkSpinParticleAnimation.animationState.removeListener(animationStateListener);
                    this.linkSpinParticleAnimation.setVisible(false);
                    resolve(true);
                }
            };

            this.linkSpinParticleAnimation.animationState.addListener(animationStateListener);
        });
    }

    /**
     * Celebra la vincita di una singola cella nel LinkSpin
     * Viene chiamata una volta per ogni cella vincente
     */
    public celebrateLinkWin(animationIndex: number): Promise<boolean> {
        return new Promise(async (resolve) => {
            // ✨ Reset della flag all'inizio di ogni celebrazione
            this.isStopped = false;
            this.celebrateLinkResolve = resolve;

            // Helper locale
            const checkStop = () => {
                if (this.isStopped || this.scene.linkGameArea.isStoppingLinkWinCelebration) {
                    resolve(true);
                    return true;
                }
                return false;
            };

            const memo = this.getMemo();
            const EXIT_MAP: Record<number, string> = {
                13: "mini_exit",
                14: "major_exit",
                15: "grand_exit"
            };

            // ✨ Controlla se è stato richiesto lo stop dopo il delay
            if (checkStop()) return;

            try {
                // Step 1: Delay iniziale basato sull'indice della cella
                await this.waitDelay(animationIndex * 1000);

                // ✨ Controlla se è stato richiesto lo stop dopo il delay
                if (checkStop()) return;

                // Step 2: Identifica se è un simbolo jackpot
                const isJackpot = memo === 13 || memo === 14 || memo === 15 || memo === 17;

                // ✨ Controlla PRIMA di fare lo shoot
                if (checkStop()) return;

                // Step 3: Fai lo shoot dell'animazione
                await this.scene.animationManager.leftSideGridAnimation?.shoot();

                // ✨ Controlla SUBITO DOPO che shoot() completa
                if (checkStop()) return;

                // Step 4: Fai l'animazione di exit se è jackpot, altrimenti particella
                if (isJackpot) {
                    const exitAnim = EXIT_MAP[memo];
                    await this.playExitAnimation(exitAnim);
                } else {
                    await this.playLinkSpinParticleAnimation();
                }

                // ✨ Controlla se è stato stoppato prima di finire
                if (checkStop()) return;

                // Step 5: Reset finale e aumento del box di vincita
                this.cleanLinkValueText();
                this.scene.linkGameArea.increaseWinBoxValue(this.multValue);
                this.background.setVisible(true);

                resolve(true);

            } catch (error) {
                // Se c'è un errore (ad es. timer cancellato), risolvi comunque
                console.warn("Celebrazione LinkWin interrotta:", error);
                resolve(true);
            }
        });
    }

    /**
     * Ferma immediatamente la celebrazione di una cella
     */
    public stopCelebrateLinkWin(): void {
        // ✨ Imposta flag per far uscire celebrateLinkWin() ai prossimi controlli
        this.isStopped = true;

        // Pulisci il testo del valore
        this.cleanLinkValueText();

        // Ferma l'animazione della particella
        this.linkSpinParticleAnimation.animationState.clearTrack(0);
        this.linkSpinParticleAnimation.setVisible(false);

        // Ferma il tween del valore se esiste
        if (this.linkSpinTextValueTween) {
            this.linkSpinTextValueTween.stop();
            this.linkSpinTextValueTween.remove();
            this.linkSpinTextValueTween.destroy();
            this.linkSpinTextValueTween = undefined;
        }

        // Ferma l'animazione spine del simbolo
        this.stopAnimation();

        // ✨ CRUCIALE: Risolvi la promise della celebrazione SUBITO
        // Questo fa sì che celebrateLinkWin() si risolva anche se è bloccata in await
        if (this.celebrateLinkResolve) {
            this.celebrateLinkResolve(true);
            this.celebrateLinkResolve = undefined;
        }

        // Cancella il timer in sospeso
        if (this.timerCelebrateLinkWin) {
            this.timerCelebrateLinkWin.destroy();
            this.timerCelebrateLinkWin = undefined;
        }
    }

    private playExitAnimation(animation: string): Promise<boolean> {
        return new Promise(async (resolve) => {
            await this.playLinkSpinParticleAnimation();

            const overlaySpineIndex = 19;
            const overlaySpine = this.getSpine(overlaySpineIndex);
            const animations = this.getLinkSpinSymbolSpineRaw(overlaySpineIndex).animations;

            if (!overlaySpine || !animations) return resolve(true);

            const animIndex = animations.indexOf(animation);
            if (animIndex === -1) return resolve(true);

            overlaySpine.animationState.clearTrack(1);
            const trackEntry = overlaySpine.animationState.addAnimation(1, animations[animIndex], false);

            if (trackEntry) {
                trackEntry.alpha = 1;
                trackEntry.timeScale = 2;
                trackEntry.listener = {
                    complete: () => {
                        overlaySpine.skeleton.setToSetupPose();
                        resolve(true);
                    }
                };
            } else {
                resolve(true);
            }
        });
    }

    private waitDelay(ms: number): Promise<void> {
        return new Promise(res => {
            this.timerCelebrateLinkWin = this.scene.time.delayedCall(ms, res);
        });
    }

    private cleanLinkValueText(): void {
        this.linkSpineValueText.setVisible(false);
        this.linkSpineValueText.setText("");
        this.linkSpineValueText.setScale(1);
    }

    // reset eventuale tween vecchio (se lo usavi ancora)
    public startCoinSpin(): void {
        const memo = this.getMemo();
        const spine = this.getSpine(memo);

        if (!spine) return;

        // nascondi eventuali animazioni precedenti
        spine.animationState.clearTracks();

        // mostra solo questo spine (in caso alcuni fossero hidden)
        this.linkSpinSymbolSpines.forEach(s => s.setVisible(false));
        spine.setVisible(true);

        // verifica che l'animazione "anim" esista
        const animations = this.getLinkSpinSymbolSpineRaw(memo).animations;

        if (!animations || !animations.includes("rotation")) {
            console.warn(`LinkCell: anima "rotation" non trovata per memo: ${memo}`);
            return;
        }

        // ✨ avvia l'animazione "rotation" in loop infinito
        const trackentry = spine.animationState.setAnimation(0, "rotation", true);
        trackentry.timeScale = 2; // velocizza l'animazione
    }

    public stopCoinSpin(): void {
        const memo = this.getMemo();
        const spine = this.getSpine(memo);

        if (!spine) return;

        // stop animazione
        spine.animationState.clearTrack(0);
        spine.skeleton.setToSetupPose();
    }
}